import { isSoftwareProject } from '../../utils/projectType';
import React, { useEffect, useState, useCallback, useId } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RequestAccessCard } from '../../components/RequestAccessCard';
import { Skeleton, SkeletonCard } from '../../components/Skeleton';
import { getTask, updateTask, type Task } from '../../packages/api-client/pm-api';
import { getUsers, type User, getMilestones, type Milestone, getProject } from '../../packages/api-client/pm-api';
import { TimeLog } from '../../components/TimeLog';
import { TaskActivityFeed } from '../../components/TaskActivityFeed';
import { AttachmentsSection } from '../../components/AttachmentsSection';
import { usePageTitle } from '../../hooks/usePageTitle';
import { StateView } from '../../packages/ui';
import { useHasPermission } from '../../packages/permission-guard';
import './TaskDetailPage.css';
// Sections follow the concept's task dialog (openTask), composed as a routed page.
import { TaskDetailHeader, TaskSummary, type TaskPerms } from './task/Identity';
import { DescriptionField } from './task/Objective';
import { SubtaskList } from './task/Hierarchy';
import { DependencyList } from './task/Dependencies';
import { CommentThread, useComments } from './task/Discussion';
import { DeliveryEvidence } from './task/DeliveryEvidence';
import { AgentRuns } from './task/AgentRuns';
import { AgentHandoff, CommitsSection } from './task/AgentHandoff';
import { EstimateFields, RecurrenceField } from './task/TimeRecurrence';
import { useTaskActions } from './task/Actions';

export { CommitsSection, DependencyList };

/** One labelled task-detail panel (a region landmark named by its h2). */
function Section({ title, className = '', children }: { title: string; className?: string; children: React.ReactNode }) {
    const headingId = useId();
    return (
        <section className={`task-section ${className}`} aria-labelledby={headingId}>
            <h2 id={headingId} className="task-section-title">{title}</h2>
            {children}
        </section>
    );
}

// ─── TaskDetailPage ──────────────────────────────────────────────────────────

export function TaskDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [task, setTask] = useState<Task | null>(null);
    usePageTitle(task?.title ?? 'Task');
    // UI mirror of the backend's /tasks/{id} route gates (tasks:update/create/delete);
    // the server still enforces every write.
    const perms: TaskPerms = {
        update: useHasPermission('tasks', 'update'),
        create: useHasPermission('tasks', 'create'),
        remove: useHasPermission('tasks', 'delete'),
    };
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [projectMilestones, setProjectMilestones] = useState<Milestone[]>([]);
    // Parent task title for the "this is a subtask of …" breadcrumb. Lazy-
    // loaded after the task itself arrives because the URL doesn't carry
    // parent_id — we only know to fetch it once we know what task we're on.
    const [parentTask, setParentTask] = useState<Pick<Task, 'id' | 'title'> | null>(null);
    const [projectName, setProjectName] = useState<string>();
    const [softwareProjectId, setSoftwareProjectId] = useState<string>();
    const [projectError, setProjectError] = useState(false);
    const { contextMenu, headerActions, modals, deleted, handleRestore, busy } = useTaskActions({ id, task, setTask, perms });
    const comments = useComments(id ?? '');

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setSoftwareProjectId(undefined);
        setProjectError(false);
        getTask(id).then(t => {
            if (cancelled) return;
            setTask(t);
            setLoading(false);
            // Load this task's project milestones for the sidebar selector.
            // Kicked off here so we have project_id (the task carries it,
            // the URL doesn't).
            if (t?.project_id) {
                getMilestones(t.project_id).then(setProjectMilestones).catch(() => { });
                // Load the project type before showing software-specific tools.
                getProject(t.project_id).then(p => {
                    if (cancelled) return;
                    setProjectName(p?.name);
                    setSoftwareProjectId(p && isSoftwareProject(p) ? t.project_id : undefined);
                }).catch(() => { if (!cancelled) setProjectError(true); });
            }
            // Lazy-load just enough of the parent (title) for the "this is a
            // subtask of X" breadcrumb. Parent might have been deleted; on
            // failure we silently drop the breadcrumb rather than showing
            // a stale id.
            if (t?.parent_id) {
                getTask(t.parent_id)
                    .then(p => setParentTask({ id: p.id, title: p.title }))
                    .catch(() => setParentTask(null));
            } else {
                setParentTask(null);
            }
        }).catch(() => {
            // 404 (no access or missing). Stop the spinner; the !task fallback
            // renders the request-access card.
            setLoading(false);
        });
        getUsers().then(setUsers).catch(() => { });
        return () => { cancelled = true; };
    }, [id]);

    const save = useCallback(async (patch: Partial<Task>) => {
        if (!id || !task) return;
        setSaving(true);
        const updated = await updateTask(id, patch);
        setTask(updated);
        setSaving(false);
    }, [id, task]);

    if (loading) return (
        <div className="task-sheet" aria-busy="true">
            <Skeleton width={220} height={14} />
            <Skeleton width="70%" height={32} />
            <Skeleton width={240} height={28} radius="var(--radius-md)" />
            <SkeletonCard height={140} />
            <SkeletonCard height={200} />
        </div>
    );
    if (!task) return <RequestAccessCard resourceType="task" resourceId={id!} />;
    if (deleted) return (
        <StateView state="empty" title="Task deleted"
            message={`"${task.title}" no longer appears in lists or search. Restore it to bring it back as it was.`}
            action={<>
                <button type="button" className="btn btn-primary btn-md" onClick={handleRestore} disabled={busy}>Restore task</button>
                <Link className="btn btn-secondary btn-md" to={`/projects/${task.project_id}?tab=Tasks`}>Back to project tasks</Link>
            </>} />
    );

    const software = softwareProjectId === task.project_id;

    return (
        <article className="task-sheet" aria-label={task.title}>
            <TaskDetailHeader task={task} projectName={projectName} parentTask={parentTask} saving={saving} perms={perms}
                setTask={setTask} save={save} actions={headerActions} />
            {contextMenu}
            {projectError && <p role="alert">Project settings could not be loaded. Reload this page to check whether software tools are available.</p>}

            {/* Two columns above 1024px; one column below, the aside after the main
                column (DOM order = reading order at every width). */}
            <div className="task-layout" data-layout="two-col">
                <div className="task-main">
                    <Section title="Objective & acceptance criteria">
                        <DescriptionField task={task} setTask={setTask} save={save} readOnly={!perms.update} />
                    </Section>

                    <Section title="Dependencies">
                        <DependencyList taskId={task.id} projectID={task.project_id} canAdd={perms.create} canRemove={perms.remove} />
                    </Section>

                    <Section title="Subtasks">
                        <SubtaskList taskId={task.id} canCreate={perms.create} canUpdate={perms.update} />
                    </Section>

                    <Section title="Discussion">
                        <CommentThread taskId={task.id} {...comments} canPost={perms.create} canDelete={perms.remove} />
                    </Section>

                    <Section title="Agent handoff" className="task-section--handoff">
                        {software && <AgentRuns key={task.id} taskId={task.id} users={users} canUpdate={perms.update} />}
                        <AgentHandoff showCodeEvidence={software} taskId={task.id} comments={comments.comments} commentsError={comments.error} users={users} />
                    </Section>

                    {software && <Section title="Delivery evidence"><DeliveryEvidence key={task.id} taskId={task.id} canUpdate={perms.update} users={users} /></Section>}

                    <Section title="Files">
                        <AttachmentsSection taskId={task.id} readOnly={!perms.create} />
                    </Section>

                    <Section title="Activity">
                        <TaskActivityFeed taskId={task.id} />
                    </Section>
                </div>

                <aside className="task-aside" aria-label="Task details">
                    <TaskSummary task={task} setTask={setTask} save={save} users={users} projectMilestones={projectMilestones} perms={perms} />

                    <Section title="Time & recurrence">
                        <EstimateFields task={task} save={save} readOnly={!perms.update} />
                        {/* Time entries are open to every task reader (RequireVisibleTask only). */}
                        <TimeLog taskId={task.id} estimatedHours={task.estimated_hours} />
                        <RecurrenceField task={task} setTask={setTask} readOnly={!perms.update} />
                    </Section>
                </aside>
            </div>

            {modals}
        </article>
    );
}
